import _ from 'lodash';

/**
 * Tenant apps: admin sections that exist for one customer and live in their
 * own repository, mounted as git submodules under `admin-ui/apps/<key>/`.
 *
 * Every app ships an `app.js` manifest:
 *
 *   export default {
 *       key: 'wine-cellar-evaluation',        // unique, also the route name prefix
 *       extension: 'wine-cellar-evaluation',  // _cp_extensions key gating the app per CMS
 *       label: 'Weinkeller Aufnahme',        // sidebar label, run through $t
 *       icon: { name: 'wine-bottle', group: 'fas' },
 *       permissions: ['ex:shop'],             // administrator rights, all required
 *       routes: [{ path: '/wine-cellar-evaluation', name: 'wine-cellar-evaluation',
 *                  component: () => import('./views/List.vue'), meta: { title: '...' } }],
 *   };
 *
 * The manifest must import views lazily only, so the main bundle carries key,
 * label and icon and nothing else; the app itself is its own async chunk that
 * only a CMS with the extension attached ever downloads.
 *
 * Discovery runs at build time through webpack's require.context; in any
 * other runtime (vitest) there are no apps and the exports stay pure.
 */
const discoverApps = () => {
    let manifests;
    try {
        manifests = require.context('../../apps', true, /^\.\/[^/]+\/app\.js$/);
    } catch (error) {
        // Not a webpack build (unit tests): no apps.
        return [];
    }

    return manifests.keys().map((key) => manifests(key).default);
};

export const apps = discoverApps();

/**
 * Routes of every app, each gated the way middleware.js already understands:
 * the app's extension must be ACTIVE on the CMS (template-independent, like the
 * system-wide extensions) and the administrator must hold the app's rights.
 */
export const appRoutes = (list = apps) =>
    _.flatMap(list, (app) =>
        (app.routes || []).map((route) => ({
            ...route,
            meta: {
                context: 'main',
                ...(route.meta || {}),
                restrictions: {
                    ...(_.get(route, 'meta.restrictions') || {}),
                    activeExtensions: [app.extension],
                    permissions: app.permissions || [],
                },
            },
        }))
    );

/**
 * Sidebar entries for the apps the current CMS and administrator may use.
 * `translate` is the caller's $t, `checkRestrictions` the helper the rest of
 * the sidebar uses.
 */
export const appMenuItems = (list, { activeExtensions, checkRestrictions, translate }) =>
    list
        .filter((app) => activeExtensions.includes(app.extension) && checkRestrictions(app.permissions || []))
        .map((app) => ({
            path: _.get(app, 'routes.0.path'),
            name: app.key,
            label: translate(app.label),
            icon: app.icon,
        }));
